import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider
} from '@mui/material';
import {
  Close,
  Download,
  DeleteOutline,
  Person,
  SmartToy,
  Computer
} from '@mui/icons-material';
import { format } from 'date-fns';
import useSessionUtilizationStore from '../../store/sessionUtilizationStore';
import { SessionInfo, SessionMessage } from '../../types';

interface SessionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  sessionKey: string;
  stepLabel?: string;
  mode?: 'isolated' | 'shared' | 'run';
}

const SessionHistoryModal: React.FC<SessionHistoryModalProps> = ({
  open,
  onClose,
  sessionKey,
  stepLabel = 'Step',
  mode = 'shared'
}) => {
  const {
    sessionMessages,
    sessionInfos,
    isLoadingHistory,
    isLoadingInfo,
    historyError,
    loadSessionHistory,
    loadSessionInfo,
    clearSessionHistory,
    exportSessionData,
    clearErrors
  } = useSessionUtilizationStore();

  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    if (open && sessionKey) {
      loadSessionHistory(sessionKey);
      loadSessionInfo(sessionKey);
    }
    return () => {
      clearErrors();
      setConfirmClear(false);
    };
  }, [open, sessionKey, loadSessionHistory, loadSessionInfo, clearErrors]);

  const messages = sessionMessages[sessionKey] || [];
  const sessionInfo = sessionInfos[sessionKey];

  const handleClearHistory = async () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    
    try {
      await clearSessionHistory(sessionKey);
      setConfirmClear(false);
    } catch (error) {
      console.error('Failed to clear session history:', error);
    }
  };

  const handleExport = async () => {
    try {
      const exportData = await exportSessionData(sessionKey);
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session_${sessionKey}_${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export session data:', error);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'user':
        return <Person fontSize="small" />;
      case 'assistant':
        return <SmartToy fontSize="small" />;
      case 'system':
        return <Computer fontSize="small" />;
      default:
        return null;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'user':
        return 'primary';
      case 'assistant':
        return 'secondary';
      case 'system':
        return 'default';
      default:
        return 'default';
    }
  };

  const getModeLabel = (mode: string) => {
    switch (mode) {
      case 'shared':
        return 'Shared';
      case 'isolated':
        return 'Isolated';
      case 'run':
        return 'Run-scoped';
      default:
        return mode;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Session History - {stepLabel} ({getModeLabel(mode)})
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Session Info */}
        {sessionInfo && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Session Key: {sessionKey}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created: {format(new Date(sessionInfo.created_at), 'PPpp')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Messages: {sessionInfo.message_count}
            </Typography>
            {sessionInfo.expires_at && (
              <Typography variant="body2" color="text.secondary">
                Expires: {format(new Date(sessionInfo.expires_at), 'PPpp')}
              </Typography>
            )}
            <Chip
              label={sessionInfo.is_active ? 'Active' : 'Inactive'}
              color={sessionInfo.is_active ? 'success' : 'default'}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Messages */}
        {isLoadingHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : historyError ? (
          <Alert severity="error">{historyError}</Alert>
        ) : messages.length === 0 ? (
          <Alert severity="info">No messages in this session yet.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {messages.map((message, index) => (
              <Paper
                key={message.message_id || index}
                elevation={1}
                sx={{
                  p: 2,
                  bgcolor: message.role === 'user' ? 'grey.50' : 'background.paper'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getRoleIcon(message.role)}
                    <Chip
                      label={message.role.charAt(0).toUpperCase() + message.role.slice(1)}
                      color={getRoleColor(message.role) as any}
                      size="small"
                    />
                    {message.run_instance_id && (
                      <Typography variant="caption" color="text.secondary">
                        Run #{message.run_instance_id.slice(0, 8)}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(message.created_at), 'pp')}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: message.role === 'system' ? 'monospace' : 'inherit'
                  }}
                >
                  {message.content}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* Clear Confirmation */}
        {confirmClear && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setConfirmClear(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="warning"
                  onClick={handleClearHistory}
                >
                  Confirm Clear
                </Button>
              </Box>
            }
          >
            Are you sure you want to clear all messages in this session? This action cannot be undone.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<DeleteOutline />}
          onClick={handleClearHistory}
          color="error"
          disabled={isLoadingHistory || messages.length === 0}
        >
          {confirmClear ? 'Click Again to Confirm' : 'Clear History'}
        </Button>
        <Button
          startIcon={<Download />}
          onClick={handleExport}
          disabled={isLoadingHistory || messages.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionHistoryModal;